import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { saveUserOnboarding } from '../db';
import { toast } from '../utils/feedback';
import { Dumbbell } from 'lucide-react';

const OnboardingScreen = ({ onComplete }) => {
  const { currentUser, logout } = useAuth();
  const [experience, setExperience] = useState('Débutant');
  const [frequency, setFrequency] = useState('1-2 fois');
  const [goal, setGoal] = useState('Masse musculaire');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let firstName = '';
      let lastName = '';
      if (currentUser?.displayName) {
        const parts = currentUser.displayName.split(' ');
        firstName = parts[0] || '';
        lastName = parts.slice(1).join(' ') || '';
      }

      await saveUserOnboarding(currentUser.uid, {
        firstName,
        lastName,
        experience,
        frequency,
        goal
      });
      onComplete();
    } catch (err) {
      console.error("Erreur lors de la sauvegarde de l'onboarding :", err);
      toast.error(err.message || "Erreur lors de la création de l'onboarding.");
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem',
      backgroundColor: 'var(--bg-color)',
      zIndex: 9999
    }}>
      {/* Decorative background blobs */}
      <div style={{ position: 'fixed', top: '10%', left: '20%', width: '300px', height: '300px', background: 'var(--primary-color)', borderRadius: '50%', filter: 'blur(100px)', opacity: 'var(--blob-opacity)', pointerEvents: 'none' }} />
      <div style={{ position: 'fixed', bottom: '10%', right: '20%', width: '250px', height: '250px', background: 'var(--secondary-color)', borderRadius: '50%', filter: 'blur(100px)', opacity: 'var(--blob-opacity)', pointerEvents: 'none' }} />

      <div style={{ 
        width: '100%', maxWidth: '520px', 
        background: 'var(--card, #1c1a27)',
        border: '1px solid var(--border, rgba(255,255,255,0.08))',
        borderRadius: '24px',
        padding: '2rem',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        position: 'relative',
        overflow: 'hidden',
        zIndex: 10
      }}>
        {/* Shimmer effect line */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, transparent, var(--primary-color), transparent)', opacity: 0.5 }} />

        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
            <img loading="lazy" src="/logos/logo-officiel-transparent.png" alt="Logo Masse-terclass" style={{ height: '90px', filter: 'drop-shadow(0 10px 15px rgba(0,0,0,0.4))' }} />
          </div>
          <h2 style={{ 
            fontSize: '2rem', 
            background: 'linear-gradient(to right, var(--text-primary), var(--text-secondary))', 
            WebkitBackgroundClip: 'text', 
            WebkitTextFillColor: 'transparent',
            margin: 0, marginBottom: '0.5rem'
          }}>
            Mieux te connaître
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>
            Quelques infos pour personnaliser ton expérience
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-secondary)', marginBottom: '0.8rem', display: 'block' }}>Expérience</label>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {['Débutant', 'Intermédiaire', 'Avancé'].map(opt => (
                <div
                  key={opt}
                  onClick={() => setExperience(opt)}
                  style={{
                    flex: 1, minWidth: '100px', padding: '0.8rem 0.5rem', textAlign: 'center', borderRadius: '12px', cursor: 'pointer', fontSize: '0.9rem', transition: 'all 0.2s',
                    border: `1px solid ${experience === opt ? 'var(--primary-color)' : 'var(--glass-border)'}`,
                    backgroundColor: experience === opt ? 'rgba(168, 159, 207, 0.15)' : 'var(--glass-input-bg)',
                    color: experience === opt ? 'var(--text-primary)' : 'var(--text-secondary)'
                  }}
                >
                  {opt}
                </div>
              ))}
            </div>
          </div>
          
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-secondary)', marginBottom: '0.8rem', display: 'block' }}>Fréquence visée</label>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {['1-2 fois', '3-4 fois', '5+ fois'].map(opt => (
                <div
                  key={opt}
                  onClick={() => setFrequency(opt)}
                  style={{
                    flex: 1, minWidth: '80px', padding: '0.8rem 0.5rem', textAlign: 'center', borderRadius: '12px', cursor: 'pointer', fontSize: '0.9rem', transition: 'all 0.2s',
                    border: `1px solid ${frequency === opt ? 'var(--primary-color)' : 'var(--glass-border)'}`,
                    backgroundColor: frequency === opt ? 'rgba(168, 159, 207, 0.15)' : 'var(--glass-input-bg)',
                    color: frequency === opt ? 'var(--text-primary)' : 'var(--text-secondary)'
                  }}
                >
                  {opt}
                </div>
              ))}
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-secondary)', marginBottom: '0.8rem', display: 'block' }}>Objectif principal</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              {['Masse musculaire', 'Perte de poids', 'Force', 'Santé globale'].map(opt => (
                <div
                  key={opt}
                  onClick={() => setGoal(opt)}
                  style={{
                    padding: '0.8rem 0.5rem', textAlign: 'center', borderRadius: '12px', cursor: 'pointer', fontSize: '0.9rem', transition: 'all 0.2s',
                    border: `1px solid ${goal === opt ? 'var(--primary-color)' : 'var(--glass-border)'}`,
                    backgroundColor: goal === opt ? 'rgba(168, 159, 207, 0.15)' : 'var(--glass-input-bg)',
                    color: goal === opt ? 'var(--text-primary)' : 'var(--text-secondary)'
                  }}
                >
                  {opt}
                </div>
              ))}
            </div>
          </div>

          <button 
            type="submit" 
            style={{ 
              width: '100%', marginTop: '1rem', padding: '1rem', color: '#fff',
              borderRadius: '12px', fontSize: '1.1rem', fontWeight: 'bold',
              background: 'linear-gradient(135deg, var(--primary-color), #8c7fbf)',
              border: 'none', boxShadow: '0 4px 15px rgba(168, 159, 207, 0.3)',
              cursor: 'pointer', transition: 'opacity 0.2s',
              opacity: loading ? 0.7 : 1
            }}
            disabled={loading}
          >
            {loading ? 'Création...' : "C'est parti !"}
          </button>

          <button
            type="button"
            onClick={logout}
            style={{
              background: 'none', border: 'none', color: 'var(--text-secondary)',
              textDecoration: 'underline', cursor: 'pointer', marginTop: '0.5rem',
              fontSize: '0.9rem'
            }}
          >
            Annuler et se déconnecter
          </button>
        </form>
      </div>
    </div>
  );
};

export default OnboardingScreen;
